import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/store';
import { createMutation, MutationCreate } from '../../services/mutationService';
import { Item } from '../../types/item';
import { fetchItemsAsync } from '../inventory/inventorySlice';
import BranchSelect from '../branch/BranchSelect';

interface CreateMutationModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const CreateMutationModal: React.FC<CreateMutationModalProps> = ({ onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const { items, loading: itemsLoading } = useSelector((state: RootState) => state.inventory);
  
  const [toBranchId, setToBranchId] = useState<number | undefined>(undefined);
  const [selectedItemId, setSelectedItemId] = useState<number | ''>('');
  const [qty, setQty] = useState<number>(1);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [itemSearch, setItemSearch] = useState('');
  
  useEffect(() => {
    // Load items if not already loaded
    if (items.length === 0) {
      dispatch(fetchItemsAsync() as any);
    }
  }, [dispatch, items.length]);
  
  const selectedItem = items.find(i => i.id === selectedItemId);
  
  const filteredItems = items.filter(item => {
    if (!itemSearch) return true;
    const search = itemSearch.toLowerCase();
    return (
      item.name.toLowerCase().includes(search) ||
      item.sku.toLowerCase().includes(search) ||
      (item.brand && item.brand.toLowerCase().includes(search))
    );
  });
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!toBranchId) {
      alert('Pilih cabang tujuan terlebih dahulu');
      return;
    }
    
    if (!selectedItemId) {
      alert('Pilih item terlebih dahulu');
      return;
    }
    
    if (qty <= 0) {
      alert('Qty harus lebih dari 0');
      return;
    }
    
    if (!selectedItem) {
      alert('Item tidak ditemukan');
      return;
    }
    
    if (qty > selectedItem.stock) {
      alert(`Stock tidak mencukupi!\nItem: ${selectedItem.name}\nStock tersedia: ${selectedItem.stock}\nDiminta: ${qty}`);
      return;
    }
    
    setLoading(true);
    try {
      const mutationData: MutationCreate = {
        item_id: selectedItemId as number,
        to_branch_id: toBranchId,
        qty,
        notes: notes || undefined,
      };
      
      await createMutation(mutationData);
      alert(`Mutasi berhasil dibuat!\nItem: ${selectedItem.name}\nQty: ${qty}\nKe Cabang: ${toBranchId}`);
      onSuccess();
    } catch (error: any) {
      const errorMsg = error.response?.data?.detail || error.message || 'Terjadi kesalahan';
      alert(`Error membuat mutasi:\n${errorMsg}`);
      console.error('Mutation error:', error);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
        <div className="p-6 border-b flex justify-between items-center">
          <h2 className="text-xl font-bold">Buat Mutasi Stok</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">
            ×
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6">
          {/* To Branch */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Ke Cabang *</label>
            <BranchSelect
              value={toBranchId}
              onChange={setToBranchId}
              required={true}
              disabled={loading}
            />
          </div>
          
          {/* Item Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              Pilih Item 
              {!itemsLoading && items.length > 0 && (
                <span className="text-gray-500 font-normal ml-2">
                  ({itemSearch ? `${filteredItems.length} dari ${items.length}` : `${items.length} items`})
                </span>
              )}
            </label>
            <input
              type="text"
              placeholder="Cari berdasarkan nama, SKU, atau brand..."
              value={itemSearch}
              onChange={(e) => setItemSearch(e.target.value)}
              className="w-full px-3 py-2 border rounded mb-2"
            />
            {itemsLoading ? (
              <div className="w-full px-3 py-2 border rounded bg-gray-50 text-gray-500">
                Memuat data items...
              </div>
            ) : (
              <select
                value={selectedItemId}
                onChange={(e) => setSelectedItemId(e.target.value ? parseInt(e.target.value) : '')}
                className="w-full px-3 py-2 border rounded max-h-48 overflow-y-auto"
                required
                disabled={itemsLoading}
              >
                <option value="">-- Pilih Item --</option>
                {filteredItems.length > 0 ? (
                  filteredItems.map(item => (
                    <option key={item.id} value={item.id}>
                      {item.name} ({item.sku}) - Stock: {item.stock}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>
                    {items.length === 0 ? 'Tidak ada items tersedia' : 'Tidak ada hasil'}
                  </option>
                )}
              </select>
            )}
            {itemSearch && filteredItems.length === 0 && items.length > 0 && (
              <p className="text-sm text-orange-500 mt-1">
                ❌ Tidak ada item yang sesuai dengan pencarian "{itemSearch}"
              </p>
            )}
            {itemSearch && filteredItems.length > 0 && (
              <p className="text-sm text-green-600 mt-1">
                ✓ Ditemukan {filteredItems.length} item
              </p>
            )}
            {items.length === 0 && !itemsLoading && (
              <p className="text-sm text-red-500 mt-1">
                ⚠️ Tidak ada items di inventory. Silakan tambahkan item terlebih dahulu.
              </p>
            )}
          </div>
          
          {/* Quantity */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Quantity</label>
            <input
              type="number"
              min="1"
              max={selectedItem?.stock || 999}
              value={qty}
              onChange={(e) => setQty(parseInt(e.target.value))}
              className="w-full px-3 py-2 border rounded"
              required
            />
            {selectedItem && (
              <p className="text-sm text-gray-500 mt-1">
                Available stock: {selectedItem.stock}
              </p>
            )}
          </div>
          
          {/* Notes */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">Catatan</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border rounded"
              placeholder="Catatan tambahan (opsional)"
            />
          </div>
          
          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 bg-gray-200 hover:bg-gray-300 rounded"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={loading || !selectedItemId}
              className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {loading ? 'Memproses...' : 'Buat Mutasi'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateMutationModal;
